import type {AxiosError} from 'axios';
import {useCallback, useEffect, useState} from 'react';

import {paymentMethodService} from '@/services/paymentMethodService';
import {userService} from '@/services/userService';
import type {
  AdminAfriexPerson,
  AfriexPaymentMethod,
  AfriexUserBalances,
} from '@/types';
import {AfriexTransactionChannel} from '@/types';
import {PaymentMethodStatus} from '@/types/AfriexPaymentMethod';
import type {
  AfriexSecurityNote,
  AfriexSecurityNotesQueryParams,
} from '@/types/Notes';
import {getErrorMessage} from '@/utils/errorMessage';
import {logger} from '@/utils/logger';
import {showErrorMessage} from '@/utils/showErrorMessage';

import type {UserActiveCard, VirtualCardPaymentMethod} from './types';

const useUserDetails = (userId: string) => {
  const [userDetails, setUserDetails] = useState<AdminAfriexPerson>(
    {} as AdminAfriexPerson,
  );
  const [isLoadingUserDetails, setIsLoadingUserDetails] = useState(false);
  const [isLoadingWalletBalances, setIsLoadingWalletBalances] =
    useState<boolean>(false);
  // Card and virtual-bank-account loads have their own flags so they never
  // block the page-level spinner (which gates the wallet balance from showing).
  const [isLoadingActiveCards, setIsLoadingActiveCards] =
    useState<boolean>(false);
  const [isLoadingVirtualBankAccounts, setIsLoadingVirtualBankAccounts] =
    useState<boolean>(false);
  const [walletBalances, setWalletBalances] = useState<AfriexUserBalances>({});
  const [securityNotes, setSecurityNotes] = useState<AfriexSecurityNote[]>([]);
  const [isLoadingSecurityNotes, setIsLoadingSecurityNotes] =
    useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [limit] = useState<number>(6); // Set your limit here
  const [hasMoreNotes, setHasMoreNotes] = useState(true);
  const [activeCards, setActiveCards] = useState<UserActiveCard[]>([]);
  const [virtualCard, setVirtualCard] =
    useState<VirtualCardPaymentMethod | null>(null);
  const [virtualCardBalance, setVirtualCardBalance] = useState<string>('0');
  const [isAddingSecurityNote, setIsAddingSecurityNote] = useState(false);
  const [virtualBankAccounts, setVirtualBankAccounts] = useState<
    AfriexPaymentMethod[]
  >([]);
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showComplianceModal, setShowComplianceModal] =
    useState<boolean>(false);
  const toggleModal = () => setShowModal(!showModal);
  const toggleComplianceModal = () =>
    setShowComplianceModal(!showComplianceModal);

  const fetchWalletBalances = useCallback(
    async (signal?: AbortSignal) => {
      try {
        if (!userId) {
          return;
        }
        setIsLoadingWalletBalances(true);
        const balances = await userService.getUserWalletBalance(userId, signal);
        setWalletBalances(balances);
      } catch (e) {
        // The request was superseded (userId changed or the screen unmounted);
        // a newer request now owns the state, so ignore this one.
        if (signal?.aborted) {
          return;
        }
        const err = e as AxiosError<{message?: string}>;
        logger.log('fetching user wallet balances error:', err);
        const errorMessage = getErrorMessage(e, '');
        showErrorMessage('Failed to fetch user balances:' + errorMessage);
      } finally {
        // Always clear the loading flag for the request that is still current,
        // regardless of whether balances came back empty.
        if (!signal?.aborted) {
          setIsLoadingWalletBalances(false);
        }
      }
    },
    [userId],
  );

  const fetchSecurityNotes = useCallback(
    async (params?: AfriexSecurityNotesQueryParams) => {
      try {
        if (!userId) {
          return;
        }

        if (params?.page === 0) {
          setIsLoadingSecurityNotes(true);
          // Clear existing notes when starting a new fetch from page 0
          setSecurityNotes([]);
        } else {
          setIsFetchingMore(true);
        }

        const response = await userService.getUserSecurityNotes(userId, {
          ...params,
        });

        setSecurityNotes((prevNotes) =>
          // If page is 0, replace notes, otherwise append
          params?.page === 0
            ? [...response.data]
            : [...prevNotes, ...response.data],
        );

        if (response.data.length < (params?.limit || limit)) {
          setHasMoreNotes(false);
        }
      } catch (e) {
        logger.error('fetching user security notes error:', e);
        const errorMessage = getErrorMessage(e, '');
        showErrorMessage('Failed to fetch user security notes:' + errorMessage);
      } finally {
        setIsLoadingSecurityNotes(false);
        setIsFetchingMore(false);
      }
    },
    [userId, limit],
  );

  const addSecurityNote = useCallback(
    async (securityNoteInput: string): Promise<void> => {
      try {
        if (!userId) {
          return;
        }
        setIsAddingSecurityNote(true);
        const response = await userService.createSecurityNote({
          note: securityNoteInput,
          user: userId,
        });
        if (response?.adminInfo) {
          setSecurityNotes((prevNotes) => [response, ...prevNotes]);
          setTimeout(() => {
            setIsAddingSecurityNote(false);
          }, 2000);
        }
      } catch (e) {
        const errorMessage = getErrorMessage(e, '');
        showErrorMessage('Failed to add user security notes:' + errorMessage);
        setIsAddingSecurityNote(false);
      }
    },
    [userId],
  );

  const fetchUserDetails = useCallback(async (): Promise<void> => {
    try {
      if (!userId) {
        return;
      }
      setIsLoadingUserDetails(true);
      const response = await userService.getUser(userId);
      setUserDetails(response);
    } catch (e) {
      const errorMessage = getErrorMessage(e, '');
      showErrorMessage('Failed to fetch user details:' + errorMessage);
    } finally {
      setIsLoadingUserDetails(false);
    }
  }, [userId]);

  const fetchUserActiveCardList = useCallback(async (): Promise<void> => {
    try {
      if (!userId) {
        return;
      }
      setIsLoadingActiveCards(true);
      const response = await paymentMethodService.getUserActiveCards(userId);
      setActiveCards(response);
    } catch (e) {
      const err = e as AxiosError<{message?: string}>;
      const errorMessage = err.message
        ? err.message
        : err.response?.data.message;
      logger.log('Failed to fetch user details:' + errorMessage);
    } finally {
      setIsLoadingActiveCards(false);
    }
  }, [userId]);

  const fetchVirtualCards = useCallback(async () => {
    try {
      if (!userId) {
        return;
      }
      const response = await paymentMethodService.getUserPaymentMethods(
        userId,
        AfriexTransactionChannel.VIRTUAL_CARD,
      );

      const virtualCards = response.data as VirtualCardPaymentMethod[];
      //return only active cards
      const activeCards = virtualCards.filter(
        (card) => card.status === PaymentMethodStatus.ACTIVE,
      );
      setVirtualCard(activeCards?.[0] || null);
    } catch (e) {
      const errorMessage = getErrorMessage(e, '');
      showErrorMessage('Failed to fetch user virtual cards:' + errorMessage);
    }
  }, [userId]);

  const getVirtualCardBalance = useCallback(async () => {
    try {
      if (!userId || !virtualCard) {
        return;
      }
      const virtualCardId = virtualCard.id;
      const response = await paymentMethodService.getVirtualCardBalance(
        userId,
        virtualCardId,
      );
      setVirtualCardBalance(response?.balance || '0');
    } catch (e) {
      const errorMessage = getErrorMessage(e, '');
      showErrorMessage('Failed to fetch virtual card balance:' + errorMessage);
    }
  }, [userId, virtualCard]);

  const updateCardBankRegulatedStatus = async (
    userId: string,
    paymentMethodId: string,
    isRegulated: boolean,
  ): Promise<void> => {
    try {
      setIsLoadingUserDetails(true);
      await paymentMethodService.updateCardBankRegulatedStatus(
        userId,
        paymentMethodId,
        isRegulated,
      );
      window.location.reload();
      setIsLoadingUserDetails(false);
    } catch (e) {
      setIsLoadingUserDetails(false);
      const errorMessage = getErrorMessage(e, '');
      showErrorMessage(
        'Failed to update card regulated status:' + errorMessage,
      );
      logger.log('Failed to uodate card regulated status:' + errorMessage);
    }
  };

  const fetchVirtualBankAccounts = useCallback(async () => {
    try {
      if (!userId) {
        return;
      }
      setIsLoadingVirtualBankAccounts(true);
      const response = await paymentMethodService.getUserPaymentMethods(
        userId,
        AfriexTransactionChannel.VIRTUAL_BANK_ACCOUNT,
      );
      const activeAccounts = response.data.filter(
        (account) => account.status === PaymentMethodStatus.ACTIVE,
      );
      setVirtualBankAccounts(activeAccounts);
    } catch (e) {
      const errorMessage = getErrorMessage(e, '');
      showErrorMessage('Failed to fetch virtual bank accounts:' + errorMessage);
    } finally {
      setIsLoadingVirtualBankAccounts(false);
    }
  }, [userId]);

  const loadMoreSecurityNotes = () => {
    setPage((prevPage) => prevPage + 1);
  };

  // Reset pagination when userId changes
  useEffect(() => {
    setPage(0);
    setHasMoreNotes(true);
    setSecurityNotes([]);
  }, [userId]);

  // Fetch security notes when page changes
  useEffect(() => {
    // Fire-and-forget: fetchSecurityNotes try/catches, clears its loading
    // flags and reports through showErrorMessage, so this only stops an
    // unexpected throw becoming an unhandled rejection.
    fetchSecurityNotes({page, limit}).catch(() => {});
  }, [page, fetchSecurityNotes, limit]);

  // Fetch initial data. Each fetcher try/catches, clears its own loading flag
  // and reports through showErrorMessage or logger, so the catches here only
  // stop an unexpected throw becoming an unhandled rejection. They stay
  // separate statements rather than a Promise.all so one slow or failing
  // section still cannot hold up the others, which is today's behaviour.
  useEffect(() => {
    fetchUserDetails().catch(() => {});
    fetchUserActiveCardList().catch(() => {});
    fetchVirtualCards().catch(() => {});
    fetchVirtualBankAccounts().catch(() => {});
  }, [
    fetchUserDetails,
    fetchUserActiveCardList,
    fetchVirtualCards,
    fetchVirtualBankAccounts,
  ]);

  // Wallet balances load independently with cancellation so a stale response
  // (e.g. after navigating to a different user) can never overwrite the
  // current balance or leave it stuck on "Loading...".
  useEffect(() => {
    const controller = new AbortController();
    // Fire-and-forget: fetchWalletBalances try/catches (including the aborted
    // case) and clears its own loading flag, so this only stops an unexpected
    // throw becoming an unhandled rejection.
    fetchWalletBalances(controller.signal).catch(() => {});
    return () => controller.abort();
  }, [fetchWalletBalances]);

  useEffect(() => {
    if (!virtualCard) {
      return;
    }
    // Fire-and-forget: getVirtualCardBalance try/catches and reports through
    // showErrorMessage, so this only stops an unexpected throw becoming an
    // unhandled rejection.
    getVirtualCardBalance().catch(() => {});
  }, [virtualCard]);

  return {
    addSecurityNote,
    isAddingSecurityNote,
    isLoadingWalletBalances,
    isLoadingActiveCards,
    isLoadingVirtualBankAccounts,
    isLoadingSecurityNotes,
    isLoadingUserDetails,
    securityNotes,
    loadMoreSecurityNotes,
    hasMoreNotes,
    isFetchingMore,
    showModal,
    toggleModal,
    fetchUserDetails,
    user: userDetails,
    walletBalances,
    activeCards,
    updateCardBankRegulatedStatus,
    toggleComplianceModal,
    showComplianceModal,
    virtualCard,
    virtualCardBalance,
    virtualBankAccounts,
  };
};

export default useUserDetails;
